import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  History,
  RefreshCw,
  Search,
  Eye,
  User,
  FileText,
  Settings,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { formatDate, formatRelativeTime } from '../lib/utils';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_ICONS = {
  case_viewed: Eye,
  case_assigned: User,
  case_decided: CheckCircle,
  evidence_viewed: FileText,
  setting_changed: Settings,
  user_created: User,
  escalation: AlertTriangle,
};

const ACTION_COLORS = {
  case_viewed: 'bg-blue-100 text-blue-800',
  case_assigned: 'bg-purple-100 text-purple-800',
  case_decided: 'bg-emerald-100 text-emerald-800',
  evidence_viewed: 'bg-slate-100 text-slate-800',
  setting_changed: 'bg-amber-100 text-amber-800',
  user_created: 'bg-indigo-100 text-indigo-800',
  escalation: 'bg-red-100 text-red-800',
};

export function AuditTrail() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (actionFilter !== 'all') params.append('action', actionFilter);
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', '200');
      
      const response = await axios.get(`${API_URL}/api/admin/audit-trail?${params}`);
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
  }, [actionFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAuditLogs();
  };

  const openDetails = (log) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const getActionIcon = (action) => {
    const Icon = ACTION_ICONS[action] || History;
    return <Icon className="h-4 w-4" />;
  };

  const formatActionName = (action) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="audit-trail-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-trail">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Audit Trail</h1>
          <p className="text-muted-foreground">View all system actions and user activities</p>
        </div>
        <div className="flex gap-2">
          <PageHelp pageKey="auditTrail" />
          <Button variant="outline" onClick={fetchAuditLogs}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Activity Log
          </CardTitle>
          <CardDescription>
            Complete audit trail of all actions taken in the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by user, entity, or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button type="submit" variant="secondary">Search</Button>
            </form>
            
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="case_viewed">Case Viewed</SelectItem>
                <SelectItem value="case_assigned">Case Assigned</SelectItem>
                <SelectItem value="case_decided">Case Decided</SelectItem>
                <SelectItem value="evidence_viewed">Evidence Viewed</SelectItem>
                <SelectItem value="setting_changed">Setting Changed</SelectItem>
                <SelectItem value="user_created">User Created</SelectItem>
                <SelectItem value="escalation">Escalation</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="text-2xl font-bold">{logs.length}</div>
              <div className="text-sm text-muted-foreground">Total Records</div>
            </div>
            <div className="p-4 bg-emerald-50 rounded-lg">
              <div className="text-2xl font-bold text-emerald-700">
                {logs.filter(l => l.action === 'case_decided').length}
              </div>
              <div className="text-sm text-muted-foreground">Decisions Made</div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-700">
                {logs.filter(l => l.action === 'case_viewed').length}
              </div>
              <div className="text-sm text-muted-foreground">Cases Viewed</div>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg">
              <div className="text-2xl font-bold text-amber-700">
                {logs.filter(l => l.action === 'setting_changed').length}
              </div>
              <div className="text-sm text-muted-foreground">Settings Changed</div>
            </div>
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No audit records found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div className="text-sm">{formatDate(log.timestamp)}</div>
                            <div className="text-xs text-muted-foreground">
                              {formatRelativeTime(log.timestamp)}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center">
                            <User className="h-4 w-4 text-slate-600" />
                          </div>
                          <div>
                            <div className="font-medium text-sm">{log.actor_name || 'System'}</div>
                            <div className="text-xs text-muted-foreground truncate max-w-[150px]">
                              {log.actor_id?.substring(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-800'}>
                          <span className="flex items-center gap-1">
                            {getActionIcon(log.action)}
                            {formatActionName(log.action)}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="text-sm capitalize">{log.entity_type}</div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {log.entity_id?.substring(0, 12)}...
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground max-w-[200px] truncate">
                          {log.after_state?.status && `Status: ${log.after_state.status}`}
                          {log.after_state?.decision?.reason_codes && 
                            ` (${log.after_state.decision.reason_codes.join(', ')})`
                          }
                          {!log.after_state?.status && !log.after_state?.decision && 'View details...'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openDetails(log)}
                          data-testid={`view-audit-${log.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedLog && getActionIcon(selectedLog.action)}
              Audit Log Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Timestamp</label>
                  <p className="text-sm">{formatDate(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Action</label>
                  <p>
                    <Badge className={ACTION_COLORS[selectedLog.action] || 'bg-slate-100'}>
                      {formatActionName(selectedLog.action)}
                    </Badge>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">User</label>
                  <p className="text-sm">{selectedLog.actor_name || 'System'}</p>
                  <p className="text-xs text-muted-foreground font-mono">{selectedLog.actor_id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Entity</label>
                  <p className="text-sm capitalize">{selectedLog.entity_type}</p>
                  <p className="text-xs text-muted-foreground font-mono">{selectedLog.entity_id}</p>
                </div>
              </div>

              {selectedLog.before_state && Object.keys(selectedLog.before_state).length > 0 && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Before State</label>
                  <pre className="mt-1 p-3 bg-red-50 rounded-lg text-xs overflow-x-auto">
                    {JSON.stringify(selectedLog.before_state, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.after_state && Object.keys(selectedLog.after_state).length > 0 && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">After State</label>
                  <pre className="mt-1 p-3 bg-emerald-50 rounded-lg text-xs overflow-x-auto">
                    {JSON.stringify(selectedLog.after_state, null, 2)}
                  </pre>
                </div>
              )}

              <div className="pt-4 border-t">
                <p className="text-xs text-muted-foreground">
                  Log ID: <span className="font-mono">{selectedLog.id}</span>
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AuditTrail;
